// Helpers for picking a pickup time inside a listing's availability window.
//
// Listings store `time_window: { start, end, timezone }` where start/end are
// naive ISO strings (no offset) representing local wall-clock time in the
// listing's timezone. A pickup time sent to the backend uses the same naive
// ISO shape: "YYYY-MM-DDTHH:mm:ss".
//
// A window MAY cross midnight, in which case `end` carries the day after
// `start`. That's why the bounds below are full datetime keys rather than a
// shared date plus two times: for a wrapped window there is no single date, and
// no time-of-day min/max pair that IonDatetime can render (min 22:00 with max
// 01:00 leaves it with nothing to show). Callers pass the picked wall-clock
// time through resolvePickup, which decides which date it lands on.

import type { TimeWindow } from '../types/listing';

export interface PickupBounds {
  /** Earliest selectable pickup, "YYYY-MM-DDTHH:mm:ss", clamped to "now". */
  min: string;
  /** Latest selectable pickup, "YYYY-MM-DDTHH:mm:ss". */
  max: string;
  /** True when min and max fall on different dates. */
  crossesMidnight: boolean;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** Round minutes UP to the nearest 15-minute mark. */
function ceilTo15(minutes: number): number {
  return Math.ceil(minutes / 15) * 15;
}

/** Current local wall-clock ceiled to the next 15-minute boundary, as a naive
 *  "YYYY-MM-DDTHH:mm:ss" key. Rolls the date when it ceils past midnight. */
function nowCeiledTo15Key(): string {
  const d = new Date();
  d.setSeconds(0, 0);
  d.setMinutes(ceilTo15(d.getMinutes()));
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:00`
  );
}

/** "HH:mm:ss" out of a naive datetime key. */
export const timeOf = (key: string): string => key.split('T')[1] ?? '';

/** "YYYY-MM-DD" out of a naive datetime key. */
export const dateOf = (key: string): string => key.split('T')[0] ?? '';

const toMinutes = (time: string): number => {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

/**
 * Extract pickup bounds from a listing's time window. The minimum is clamped to
 * "now" (ceiled to the next 15-minute mark) so buyers cannot select a time in
 * the past, unless that would invert the range — for an already-finished window
 * we hand back the raw bounds rather than an empty picker.
 */
export function getPickupBounds(tw: TimeWindow): PickupBounds {
  const start = tw.start;
  const end = tw.end;

  const now = nowCeiledTo15Key();
  const min = now > start && now < end ? now : start;

  return { min, max: end, crossesMidnight: dateOf(min) !== dateOf(end) };
}

/**
 * Resolve a wall-clock time picked on the wheel to a full naive datetime inside
 * the window.
 *
 * Same-day window: the date is fixed, so the time is simply clamped.
 * Cross-midnight window: a time at or after the start belongs to the start
 * date, a time at or before the end belongs to the end date. A time in the dead
 * arc between the two snaps to whichever bound is nearer on the clock, so the
 * wheel can be spun freely without ever producing a pickup outside the window.
 */
export function resolvePickup(bounds: PickupBounds, time: string): string {
  const minDate = dateOf(bounds.min);
  const maxDate = dateOf(bounds.max);
  const minTime = timeOf(bounds.min);
  const maxTime = timeOf(bounds.max);

  if (!bounds.crossesMidnight) {
    if (time < minTime) return bounds.min;
    if (time > maxTime) return bounds.max;
    return `${minDate}T${time}`;
  }

  if (time >= minTime) return `${minDate}T${time}`;
  if (time <= maxTime) return `${maxDate}T${time}`;

  const picked = toMinutes(time);
  const pastEnd = picked - toMinutes(maxTime);
  const untilStart = toMinutes(minTime) - picked;
  return pastEnd <= untilStart ? bounds.max : bounds.min;
}
